import { useMemo } from 'react'
import { Text, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Calendar, Check, Clock, MapPin, Navigation } from '@/components/ui/icons'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog, useConfirmDialog } from '@/components/ui/ConfirmDialog'
import { EmptyState } from '@/components/ui/EmptyState'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import {
  useAcceptServiceQuote,
  useCancelServiceRequest,
  useServiceRequest,
} from '@/lib/hooks/use-services'
import { useTheme } from '@/lib/hooks/use-theme'
import { serviceCategoryById, type ServiceRequestStatus } from '@/lib/services/services-catalog'
import { formatSvDateDisplay } from '@/lib/utils/dates'
import { formatCurrency } from '@/lib/utils/format'
import { toast } from '@/lib/toast'
import { COLORS, FONTS, RADIUS, SPACING } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { ServiceCategoryIcon } from './ServiceCategoryIcon'

const STATUS_TONE: Record<ServiceRequestStatus, { fill: string; ink: string }> = {
  submitted: { fill: 'rgba(91,84,144,0.12)', ink: COLORS.purple.DEFAULT },
  matching: { fill: 'rgba(242,180,65,0.16)', ink: '#8A5A0B' },
  quoted: { fill: 'rgba(50,138,108,0.15)', ink: '#207253' },
  confirmed: { fill: 'rgba(50,138,108,0.15)', ink: '#207253' },
  in_progress: { fill: 'rgba(239,78,74,0.12)', ink: COLORS.coral.dark },
  completed: { fill: 'rgba(50,138,108,0.15)', ink: '#207253' },
  cancelled: { fill: 'rgba(94,91,105,0.12)', ink: '#5E5B69' },
}

export function ServiceRequestDetail() {
  const { t } = useTranslation()
  const router = useRouter()
  const params = useLocalSearchParams<{ requestId?: string }>()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const requestQuery = useServiceRequest(params.requestId)
  const cancelMutation = useCancelServiceRequest()
  const acceptQuoteMutation = useAcceptServiceQuote()
  const cancelDialog = useConfirmDialog()
  const acceptDialog = useConfirmDialog()
  const request = requestQuery.data
  const category = useMemo(
    () => (request ? serviceCategoryById(request.category) : null),
    [request],
  )

  if (requestQuery.isLoading) {
    return (
      <ScreenContainer variant="content">
        <View />
      </ScreenContainer>
    )
  }

  if (!request || !category) {
    return (
      <ScreenContainer variant="content">
        <EmptyState
          variant="bloom"
          title={t('services.detail.not_found_title')}
          description={t('services.detail.not_found_body')}
          action={{
            label: t('services.detail.back_to_services'),
            onPress: () => router.replace('/services' as never),
          }}
        />
      </ScreenContainer>
    )
  }

  const tone = STATUS_TONE[request.status]
  const confirmCancel = async () => {
    try {
      await cancelMutation.mutateAsync(request.id)
      cancelDialog.close()
    } catch {
      toast.error(t('services.detail.cancel_error'))
    }
  }

  const confirmQuote = async () => {
    try {
      await acceptQuoteMutation.mutateAsync(request.id)
      acceptDialog.close()
    } catch {
      toast.error(t('services.detail.accept_error'))
    }
  }

  return (
    <ScreenContainer variant="content" scroll={false}>
      <ScreenHeader
        title={t('services.detail.title')}
        showBack
        onBackPress={() => router.back()}
        style={{ paddingHorizontal: SPACING.lg, paddingTop: SPACING.md }}
      />
      <ScrollView
        contentContainerStyle={{
          padding: SPACING.lg,
          paddingTop: SPACING.md,
          paddingBottom: SPACING.xl + SPACING.lg,
          gap: SPACING.lg,
        }}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={{
            borderRadius: RADIUS.xl,
            borderWidth: 1,
            borderColor: colors.border,
            backgroundColor: colors.surface,
            padding: SPACING.lg,
            gap: SPACING.md,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm }}>
            <ServiceCategoryIcon category={category} size={24} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                numberOfLines={2}
                style={{
                  // The status pill reserves the trailing measure. At 20pt,
                  // even "Grúa y auxilio" split into two short, visually
                  // accidental lines on a 360dp phone. This remains a clear
                  // hierarchy step below the screen title while giving common
                  // category names a single, calm line.
                  fontFamily: FONTS.bodyBold,
                  fontSize: 18,
                  lineHeight: 22,
                  flexShrink: 1,
                  color: colors.textPrimary,
                }}
              >
                {t(category.titleKey)}
              </Text>
              <Text
                style={{
                  marginTop: 2,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  color: colors.textSecondary,
                }}
              >
                {t(`services.needs.${request.need}`)}
              </Text>
            </View>
            <View
              style={{
                borderRadius: RADIUS.full,
                paddingHorizontal: SPACING.sm,
                paddingVertical: SPACING.xs,
                backgroundColor: tone.fill,
              }}
            >
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: tone.ink }}>
                {t(`services.status.${request.status}`)}
              </Text>
            </View>
          </View>
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              lineHeight: 20,
              color: colors.textSecondary,
            }}
          >
            {t(`services.status_detail.${request.status}`)}
          </Text>
        </View>

        {request.quote ? (
          <View
            style={{
              gap: SPACING.sm,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: isDark ? 'rgba(255,255,255,0.11)' : 'rgba(91,84,144,0.15)',
              backgroundColor: isDark ? 'rgba(91,84,144,0.16)' : 'rgba(91,84,144,0.06)',
              padding: SPACING.md,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: SPACING.sm }}>
              <Text
                style={{
                  flex: 1,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 15,
                  color: colors.textPrimary,
                }}
              >
                {t('services.detail.quote_title')}
              </Text>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 20, color: colors.textBrand }}>
                {formatCurrency(request.quote.amountCents)}
              </Text>
            </View>
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 19,
                color: colors.textSecondary,
              }}
            >
              {request.quote.summary}
            </Text>
            {request.canAcceptQuote ? (
              <>
                <Text
                  style={{
                    fontFamily: FONTS.body,
                    fontSize: 12,
                    lineHeight: 17,
                    color: colors.textSecondary,
                  }}
                >
                  {t('services.detail.quote_confirm_note')}
                </Text>
                <Button
                  title={t('services.detail.accept_quote_cta')}
                  size="md"
                  loading={acceptQuoteMutation.isPending}
                  onPress={acceptDialog.open}
                  icon={<Check size={18} />}
                  testID="services.detail.accept-quote"
                />
              </>
            ) : request.quote.acceptedAt ? (
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.success }}>
                {t('services.detail.quote_accepted')}
              </Text>
            ) : null}
          </View>
        ) : null}

        <View style={{ gap: SPACING.sm }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 18, color: colors.textPrimary }}>
            {t('services.detail.request_title')}
          </Text>
          <View
            style={{
              gap: SPACING.md,
              borderRadius: RADIUS.xl,
              backgroundColor: colors.surfaceSubtle,
              padding: SPACING.md,
            }}
          >
            <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
              <MapPin size={19} color={COLORS.coral.DEFAULT} strokeWidth={2} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}>
                  {t('services.detail.location_label')}
                </Text>
                <Text
                  style={{
                    marginTop: 2,
                    fontFamily: FONTS.bodyBold,
                    fontSize: 14,
                    color: colors.textPrimary,
                  }}
                >
                  {request.location.label}
                </Text>
              </View>
            </View>
            {request.destination ? (
              <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
                <Navigation size={19} color={COLORS.purple.DEFAULT} strokeWidth={2} />
                <View style={{ flex: 1 }}>
                  <Text
                    style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}
                  >
                    {t('services.detail.destination_label')}
                  </Text>
                  <Text
                    style={{
                      marginTop: 2,
                      fontFamily: FONTS.bodyBold,
                      fontSize: 14,
                      color: colors.textPrimary,
                    }}
                  >
                    {request.destination.label}
                  </Text>
                </View>
              </View>
            ) : null}
            <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
              {request.timing === 'scheduled' ? (
                <Calendar size={19} color={COLORS.purple.DEFAULT} strokeWidth={2} />
              ) : (
                <Clock size={19} color={COLORS.purple.DEFAULT} strokeWidth={2} />
              )}
              <View style={{ flex: 1 }}>
                <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}>
                  {t('services.detail.when_label')}
                </Text>
                <Text
                  style={{
                    marginTop: 2,
                    fontFamily: FONTS.bodyBold,
                    fontSize: 14,
                    color: colors.textPrimary,
                  }}
                >
                  {request.timing === 'now'
                    ? t('services.detail.now')
                    : t('services.detail.scheduled', {
                        date: request.scheduledDate
                          ? formatSvDateDisplay(request.scheduledDate)
                          : '',
                        window: t(`services.form.window_${request.arrivalWindow}`),
                      })}
                </Text>
              </View>
            </View>
            <View style={{ flexDirection: 'row', gap: SPACING.sm }}>
              <Check size={19} color={COLORS.purple.DEFAULT} strokeWidth={2} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textSecondary }}>
                  {t('services.detail.details_label')}
                </Text>
                <Text
                  style={{
                    marginTop: 2,
                    fontFamily: FONTS.body,
                    fontSize: 14,
                    lineHeight: 20,
                    color: colors.textPrimary,
                  }}
                >
                  {request.details}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <View style={{ gap: SPACING.md }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 18, color: colors.textPrimary }}>
            {t('services.detail.progress_title')}
          </Text>
          {request.events.map((event, index) => (
            <View key={event.id} style={{ flexDirection: 'row', gap: SPACING.sm }}>
              <View style={{ alignItems: 'center', width: 22 }}>
                <View
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 6,
                    marginTop: 3,
                    backgroundColor:
                      index === request.events.length - 1
                        ? COLORS.coral.DEFAULT
                        : COLORS.purple.DEFAULT,
                  }}
                />
                {index < request.events.length - 1 ? (
                  <View
                    style={{
                      width: 1,
                      flex: 1,
                      minHeight: 24,
                      marginVertical: SPACING.xs,
                      backgroundColor: colors.border,
                    }}
                  />
                ) : null}
              </View>
              <View style={{ flex: 1, paddingBottom: SPACING.md }}>
                <Text
                  style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textPrimary }}
                >
                  {t(`services.status.${event.kind === 'created' ? 'submitted' : event.kind}`)}
                </Text>
                <Text
                  style={{
                    marginTop: 2,
                    fontFamily: FONTS.body,
                    fontSize: 12,
                    color: colors.textSecondary,
                  }}
                >
                  {t(`services.timeline.${event.kind === 'created' ? 'submitted' : event.kind}`)}
                </Text>
              </View>
            </View>
          ))}
        </View>

        <View style={{ gap: SPACING.sm }}>
          {request.canCancel ? (
            <Pressable
              onPress={cancelDialog.open}
              accessibilityRole="button"
              accessibilityLabel={t('services.detail.cancel_cta')}
              testID="services.detail.cancel"
              style={({ pressed }) => ({
                alignSelf: 'center',
                padding: SPACING.sm,
                opacity: pressed ? 0.65 : 1,
              })}
            >
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textError }}>
                {t('services.detail.cancel_cta')}
              </Text>
            </Pressable>
          ) : null}
        </View>
      </ScrollView>

      <ConfirmDialog
        visible={cancelDialog.visible}
        title={t('services.detail.cancel_dialog_title')}
        message={t('services.detail.cancel_dialog_body')}
        confirmLabel={t('services.detail.cancel_confirm')}
        cancelLabel={t('common.back')}
        onConfirm={() => void confirmCancel()}
        onCancel={cancelDialog.close}
        onDismiss={cancelDialog.close}
        confirmLoading={cancelMutation.isPending}
        testIDPrefix="services.cancel-request"
      />
      <ConfirmDialog
        visible={acceptDialog.visible}
        title={t('services.detail.accept_quote_dialog_title')}
        message={t('services.detail.accept_quote_dialog_body', {
          amount: request.quote ? formatCurrency(request.quote.amountCents) : '',
        })}
        confirmLabel={t('services.detail.accept_quote_confirm')}
        cancelLabel={t('common.back')}
        onConfirm={() => void confirmQuote()}
        onCancel={acceptDialog.close}
        onDismiss={acceptDialog.close}
        confirmLoading={acceptQuoteMutation.isPending}
        testIDPrefix="services.accept-quote"
      />
    </ScreenContainer>
  )
}
